import { CssBaseline, GlobalStyles, ThemeProvider, createTheme } from '@mui/material';
import { FC, PropsWithChildren } from 'react';
import { ColorsProvider } from './colors.provider';
import { Colors } from './types';

const DEFAULT_BORDER_RADIOS = '10px';

const COLORS: Colors = {
  ctas: {
    secondary: '#332053',
  },
  background: {
    main: '#ececef',
    footer: '#281932',
  },
  text: {
    primary: 'rgba(0, 0, 0, 0.87)',
    secondary: 'rgba(0, 0, 0, 0.54)',
    disabled: 'rgba(0, 0, 0, 0.38)',
    hint: 'rgba(0, 0, 0, 0.38)',
    white: '#FFFFFF',
  },
  primary: {
    main: '#00092F',
    dark: '#000620',
    light: '#333A58',
    contrast: '#FFFFFF',
  },
  secondary: {
    main: '#0053A2',
    dark: '#003A71',
    light: '#3375B4',
    contrast: '#FFFFFF',
  },
  error: {
    main: '#FF5339',
    dark: '#F82100',
    light: '#FF8A79',
    contrast: '#FFFFFF',
  },
  warning: {
    main: '#FAD141',
    dark: '#F5C006',
    light: '#FCE07F',
    contrast: '#281932',
  },
  success: {
    main: '#5BFA41',
    dark: '#49BF35',
    light: '#AFFFA1',
    contrast: '#281932',
  },
  info: {
    main: '#41D9FA',
    dark: '#33A9C2',
    light: '#75F7FF',
    contrast: '#281932',
  },
  others: {
    black: '#000000',
    blackLight: '#4C4B4B',
    blue: '#1a5fbe',
    blueLight: '#c8e7f7',
    blueMedium: '#257aea',
    blueHard: '#0c2f5b',
    gray: '#E8E8E8',
    grayLight: '#ececef',
    grayMedium: '#ACACAC',
    grayHard: '#7F7F7F',
    greenLight: '#74d292',
    greenMedium: '#61a877',
    greenHard: '#143c21',
    yellow: '#FAD141',
    yellowDark: '#FA9941',
    red: '#D93025',
    redLight: '#ffdddd',
    redMedium: '#ffabab',
    redHard: '#ff7474',
    white: '#FFFFFF',
    offwhite: '#F8F8F8',
  },
};

const MAIN_APP_THEME = createTheme({
  palette: {
    background: {
      default: COLORS.background.main,
    },
    primary: {
      main: COLORS.primary.main,
    },
    secondary: {
      main: COLORS.secondary.main,
    },
    divider: COLORS.others.grayLight,
  },
  typography: {
    fontFamily: 'CommuterSans, sans-serif',
    h1: {
      fontSize: '1.6rem !important',
      color: COLORS.primary.main,
    },
    h2: {
      fontSize: '1.4rem !important',
      color: COLORS.primary.main,
    },
    h3: {
      fontSize: '1.2rem !important',
      color: COLORS.primary.main,
    },
    h4: {
      fontSize: '1.0rem !important',
      color: COLORS.secondary.main,
      fontWeight: 'bold',
    },
    h5: {
      fontSize: '0.8rem !important',
      color: COLORS.secondary.main,
    },
    h6: {
      fontSize: '0.6rem !important',
      color: COLORS.secondary.main,
    },
    body1: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
    },
    body2: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      fontWeight: 'bold',
    },
  },
  components: {
    MuiAlert: {
      styleOverrides: {
        root: {
          'backgroundColor': COLORS.ctas.secondary,
          'color': COLORS.primary.contrast,
          'fontFamily': 'Roboto, sans-serif',
          'fontSize': '14px',
          '& .MuiAlert-standardSuccess > .MuiAlert-icon': {
            width: '18px',
            height: '18px',
          },
        },
        standardSuccess: {
          '.MuiAlert-icon': {
            color: COLORS.success.dark,
          },
        },
        standardError: {
          '.MuiAlert-icon': {
            color: COLORS.error.main,
          },
        },
        standardWarning: {
          '.MuiAlert-icon': {
            color: COLORS.warning.main,
          },
        },
        standardInfo: {
          '.MuiAlert-icon': {
            color: COLORS.primary.main,
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: '5x',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&.MuiGrid-item': {},
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          paddingBottom: '16px !important',
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          'textTransform': 'uppercase',
          '& .MuiStepLabel-labelContainer': {
            '& .Mui-active': {
              fontWeight: 'bold',
              color: COLORS.others.blueHard,
            },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          '& .required': {
            fontWeight: 'bold',
            color: COLORS.others.red,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 5,
        },
      },
    },
  },
});

const MainAppTheme: FC<PropsWithChildren> = ({ children }) => (
  <ThemeProvider theme={MAIN_APP_THEME}>
    <GlobalStyles
      styles={{
        body: {
          '& .firebase-emulator-warning': {
            display: 'none',
          },
          // it's not worth to modify firefox scrollbar
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            background: 'inherit',
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.primary.main,
            borderRadius: '10px',
          },
        },
      }}
    />
    <CssBaseline />
    <ColorsProvider colors={COLORS} borderRadiusDefault={DEFAULT_BORDER_RADIOS}>
      {children}
    </ColorsProvider>
  </ThemeProvider>
);

export { COLORS, DEFAULT_BORDER_RADIOS, MainAppTheme };
